import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { BUILDER_FORM_CONFIG } from './contactFormConfig';
import SmsConsentFieldset, { smsConsentSubmissionFields } from './SmsConsentFieldset';
import { submitHubSpotForm } from '../../services/hubspotFormSubmit';
import '../Pages/Why.css';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

/**
 * Static field schema frozen from the HubSpot Build-with-us form on 2026-08-03.
 * Internal names are HubSpot contact properties — see docs/CONTACT_FORM_V2.md.
 * Unlike the HubSpot form definition, phone is intentionally optional here.
 */
const FIELDS = [
  { name: 'firstname', label: 'First Name', type: 'text', placeholder: 'First Name', required: true, autoComplete: 'given-name' },
  { name: 'lastname', label: 'Last Name', type: 'text', placeholder: 'Last Name', required: true, autoComplete: 'family-name' },
  { name: 'email', label: 'Email', type: 'email', placeholder: 'Email', required: true, autoComplete: 'email' },
  { name: 'phone', label: 'Phone number', type: 'tel', placeholder: 'Phone', required: false, autoComplete: 'tel' },
  {
    name: 'customer_type',
    label: 'Interest Profile',
    type: 'select',
    required: true,
    placeholder: 'Please select the profile that best matches your interest in Construx',
    options: ['Developer', 'Builder', 'Individual'],
  },
  { name: 'message', label: 'Please describe your interest', type: 'textarea', placeholder: 'I would like ...', required: true },
  {
    name: 'build_state',
    label: 'Build State',
    type: 'select',
    required: true,
    placeholder: 'Where do you plan to build?',
    options: US_STATES,
  },
  { name: 'anticipated_build_date', label: 'Build Date', type: 'date', required: false },
];

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const formatPhoneNumber = (value) => {
  const cleaned = `${value}`.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (match) {
    return [match[1], match[2], match[3]].filter(Boolean).join('-');
  }
  return value;
};

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const validatePhoneNumber = (phone) => /^\d{3}-\d{3}-\d{4}$/.test(phone);

/** yyyy-mm-dd (native date input) → MM/DD/YYYY (HubSpot field format). */
const toHubSpotDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : iso;
};

function fieldError(field, value) {
  if (field.required && !value) {
    return `${field.label} field is required.`;
  }
  if (field.name === 'email' && value && !validateEmail(value)) {
    return 'Please enter a valid email address.';
  }
  if (field.name === 'phone' && value && !validatePhoneNumber(value)) {
    return 'Please enter a valid phone number (e.g. 123-123-1234).';
  }
  return null;
}

export default function BuildWithUsForm() {
  const { kicker, title, titleAccent, lede, featured, successTitle, successMessage, pageTitle, formType } =
    BUILDER_FORM_CONFIG;

  const [formValues, setFormValues] = useState({});
  const [smsConsent, setSmsConsent] = useState({ marketing: false, nonMarketing: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const location = useLocation();
  const formId = location.pathname.split('/').pop();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;
    setFormValues((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const handleInputBlur = (e) => {
    const field = FIELDS.find((f) => f.name === e.target.name);
    if (!field) return;
    const error = e.target.value ? fieldError(field, e.target.value) : null;
    setErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next[field.name] = error;
      } else {
        delete next[field.name];
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const currentErrors = {};
    FIELDS.forEach((field) => {
      const error = fieldError(field, formValues[field.name] || '');
      if (error) currentErrors[field.name] = error;
    });

    if (Object.keys(currentErrors).length) {
      setErrors(currentErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    const fields = {
      ...formValues,
      anticipated_build_date: formValues.anticipated_build_date
        ? toHubSpotDate(formValues.anticipated_build_date)
        : '',
      ...smsConsentSubmissionFields(smsConsent),
    };

    try {
      await submitHubSpotForm(formId, fields);
      track('form_submitted', { formType, formId });
      setSubmitted(true);
    } catch (err) {
      track('form_error', { formType, formId, errorType: 'submission_failed' });
      setSubmitError('There was an error submitting the form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="why-system-page grid-bg contact-form-page">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <section className="sec contact-form-page__main">
        <div className="wrap">
          <Link to="/interest" className="contact-form-page__back">
            ← All contact options
          </Link>

          <div className={`contact-form-page__shell${featured ? ' contact-form-page__shell--feat' : ''}`}>
            <div className="contact-form-page__intro">
              <span className="kicker">{kicker}</span>
              <h1 className="h2">
                {submitted ? successTitle : title}
                {!submitted && titleAccent ? <span className="rust"> {titleAccent}</span> : null}
              </h1>
              {!submitted ? <p className="lede">{lede}</p> : null}
            </div>

            <div className="contact-form-page__card">
              {submitted ? (
                <div className="contact-form-page__success">
                  <p>{successMessage}</p>
                  <div className="contact-form-page__actions">
                    <Link className="btn btn-primary lg" to="/interest">
                      Back to contact <ArrowIcon />
                    </Link>
                  </div>
                </div>
              ) : (
                <form className="cx-form" onSubmit={handleSubmit} noValidate>
                  {FIELDS.map((field) => (
                    <div className="cx-field" key={field.name}>
                      <label className="cx-label" htmlFor={field.name}>
                        {field.label}
                        {field.required ? <span className="cx-required">*</span> : null}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          id={field.name}
                          name={field.name}
                          className="cx-input"
                          value={formValues[field.name] || ''}
                          onChange={handleInputChange}
                          onBlur={handleInputBlur}
                          disabled={submitting}
                        >
                          <option value="">{field.placeholder}</option>
                          {field.options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          id={field.name}
                          name={field.name}
                          className="cx-input"
                          rows={4}
                          value={formValues[field.name] || ''}
                          placeholder={field.placeholder}
                          onChange={handleInputChange}
                          onBlur={handleInputBlur}
                          disabled={submitting}
                        />
                      ) : (
                        <input
                          id={field.name}
                          name={field.name}
                          type={field.type}
                          className="cx-input"
                          value={formValues[field.name] || ''}
                          placeholder={field.placeholder}
                          autoComplete={field.autoComplete}
                          onChange={handleInputChange}
                          onBlur={handleInputBlur}
                          disabled={submitting}
                        />
                      )}
                      {errors[field.name] ? <p className="cx-error">{errors[field.name]}</p> : null}
                    </div>
                  ))}

                  <SmsConsentFieldset consent={smsConsent} onChange={setSmsConsent} disabled={submitting} />

                  {submitError ? <p className="cx-error cx-error--banner">{submitError}</p> : null}

                  <div className="contact-form-page__actions">
                    <button type="submit" className="btn btn-primary lg" disabled={submitting}>
                      {submitting ? 'Submitting…' : 'Submit'} {!submitting ? <ArrowIcon /> : null}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
